import { pageSize } from "./../../Components/Common/styles/table";
import { createSlice, PayloadAction } from "@reduxjs/toolkit";

type PaginationProps = {
  page: number;
  pageSize: number;
};

export interface ParticipantsState {
  loading: boolean;
  participantsData: any[];
  totalParticipants: number;
  unfilteredTotal: number | null;
  paginationModel: PaginationProps;
  toggleLoader: boolean;
  sortOrder: string;
  sortColumn: string;
  siteIds: string[];
  status: string[];
  isLocked: string;
  screenerCompleted?: string;
  screenerReceived?: string;
  screenerCompletedFrom?: string;
  screenerCompletedTo?: string;
  cohortId: string[];
  tagIds: string[];
  notesQuery: string;
  searchField: string;
  searchQuery: string;
  columns: {
    label: string;
    varname: string;
    questionLabel: string;
    questionType: string;
    questionChoices: { label: string; id: string }[];
  }[];
  selectedParticipantIds: string[];
  accessibleParticipants: string[];
  customHeaderValues: { key: string; value: string | string[] }[];
}

const initialState: ParticipantsState = {
  loading: true,
  toggleLoader: false,
  paginationModel: {
    page: 0,
    pageSize: pageSize,
  },
  participantsData: [],
  totalParticipants: 0,
  unfilteredTotal: null,
  sortOrder: "",
  sortColumn: "",
  siteIds: [],
  status: [],
  isLocked: "",
  screenerReceived: "",
  screenerCompleted: "",
  screenerCompletedFrom: "",
  screenerCompletedTo: "",
  cohortId: [],
  tagIds: [],
  notesQuery: "",
  searchField: "email",
  searchQuery: "",
  columns: [],
  selectedParticipantIds: [],
  accessibleParticipants: [],
  customHeaderValues: [],
};

export const participantsSlice = createSlice({
  name: "participants",
  initialState: initialState,
  reducers: {
    setParticipantsLoader: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setParticipantsDetails: (state, action: PayloadAction<any>) => {
      state.participantsData = action.payload;
    },
    setColumnData: (state, action: PayloadAction<any>) => {
      state.columns = action.payload;
    },
    setParticipantsCount: (state, action: PayloadAction<number>) => {
      state.totalParticipants = action.payload;
    },
    setUnfilteredTotal: (state, action: PayloadAction<number | null>) => {
      state.unfilteredTotal = action.payload;
    },
    setParticipantsPage: (state, action: PayloadAction<PaginationProps>) => {
      state.paginationModel = action.payload;
    },

    setParticipantToggle: (state) => {
      state.toggleLoader = !state.toggleLoader;
    },
    setParticipantSort: (
      state,
      action: PayloadAction<{ column: string; order: string }>
    ) => {
      state.sortColumn = action.payload.column;
      state.sortOrder = action.payload.order;
      state.paginationModel.page = 0;
    },
    setParticipantFilter: (
      state,
      action: PayloadAction<{
        siteIds: string[];
        status: string[];
        isLocked: string;
        screenerCompleted?: string;
        screenerReceived?: string;
        screenerCompletedFrom?: string;
        screenerCompletedTo?: string;
        cohortId: string[];
        tagIds: string[];
        notesQuery?: string;
        customHeaderValues: { key: string; value: string | string[] }[];
      }>
    ) => {
      state.paginationModel.page = 0;
      state.siteIds = action.payload.siteIds;
      state.status = action.payload.status;
      state.isLocked = action.payload.isLocked;
      state.screenerCompleted = action.payload.screenerCompleted || "";
      state.screenerReceived = action.payload.screenerReceived || "";
      state.screenerCompletedFrom = action.payload.screenerCompletedFrom || "";
      state.screenerCompletedTo = action.payload.screenerCompletedTo || "";
      state.cohortId = action.payload.cohortId;
      state.tagIds = action.payload.tagIds;
      state.notesQuery = action.payload.notesQuery || "";
      state.customHeaderValues = action.payload.customHeaderValues;
      state.searchField = "email";
      state.searchQuery = "";
    },
    setParticipantSearch: (
      state,
      action: PayloadAction<{ searchField: string; searchQuery: string }>
    ) => {
      state.paginationModel.page = 0;
      state.searchField = action.payload.searchField;
      state.searchQuery = action.payload.searchQuery;
    },
    setCohortId: (state, action: PayloadAction<string[]>) => {
      state.cohortId = action.payload;
      state.paginationModel.page = 0;
    },
    clearParticipantFilterField: (state, action: PayloadAction<string>) => {
      const field = action.payload;
      state.paginationModel.page = 0;
      switch (field) {
        case "siteIds":
          state.siteIds = [];
          break;
        case "status":
          state.status = [];
          break;
        case "isLocked":
          state.isLocked = "";
          break;
        case "screenerCompleted":
          state.screenerCompleted = "";
          break;
        case "screenerReceived":
          state.screenerReceived = "";
          break;
        case "screenerCompletedFrom":
          state.screenerCompletedFrom = "";
          break;
        case "screenerCompletedTo":
          state.screenerCompletedTo = "";
          break;
        case "cohortId":
          state.cohortId = [];
          break;
        case "tagIds":
          state.tagIds = [];
          break;
        case "notesQuery":
          state.notesQuery = "";
          break;
        case "screenerCompletedDate":
          state.screenerCompletedFrom = "";
          state.screenerCompletedTo = "";
          break;
        default:
          if (field.startsWith("custom:")) {
            const varname = field.replace("custom:", "");
            state.customHeaderValues = state.customHeaderValues.filter(
              (v) => v.key !== varname,
            );
          }
          break;
      }
    },
    patchParticipantFilter: (
      state,
      action: PayloadAction<Partial<ParticipantsState>>,
    ) => {
      Object.assign(state, action.payload);
      state.paginationModel.page = 0;
    },
    clearAllParticipantFilters: (state) => {
      state.paginationModel.page = 0;
      state.siteIds = [];
      state.status = [];
      state.isLocked = "";
      state.screenerCompleted = "";
      state.screenerReceived = "";
      state.screenerCompletedFrom = "";
      state.screenerCompletedTo = "";
      state.cohortId = [];
      state.tagIds = [];
      state.notesQuery = "";
      state.customHeaderValues = [];
    },
    clearParticipantIds: (state) => {
      state.selectedParticipantIds = [];
      state.accessibleParticipants = [];
    },
    appendParticipantIds: (
      state,
      action: PayloadAction<{
        actionType: "single" | "multiple";
        rowId?: string;
        checked: boolean;
      }>
    ) => {
      const { actionType, rowId, checked } = action.payload;
      let newSelected = state.selectedParticipantIds;
      let participantsData = state.participantsData;

      if (actionType === "single") {
        if (checked && rowId) {
          newSelected.push(rowId);
        } else {
          newSelected = newSelected.filter((c) => c !== rowId);
        }
      } else if (actionType === "multiple") {
        participantsData.forEach((p) => {
          const found = newSelected.find((s: any) => s === p.id);
          if (!found) {
            newSelected.push(p.id);
          }
        });
      }

      state.selectedParticipantIds = newSelected;
    },
    appendAccessibleParticipants: (
      state,
      action: PayloadAction<{
        actionType: "single" | "multiple";
        rowId?: string;
        checked: boolean;
        canEdit?: boolean;
      }>
    ) => {
      const { actionType, rowId, checked, canEdit } = action.payload;
      let newAccessible = state.accessibleParticipants;
      let participantsData = state.participantsData;

      if (actionType === "single") {
        if (checked && rowId && canEdit === true) {
          newAccessible.push(rowId);
        } else if (!checked && rowId) {
          newAccessible = newAccessible.filter((p) => p !== rowId);
        }
      } else if (actionType === "multiple") {
        const accessible = participantsData.filter(
          (p) => p.canAssignConsent === true
        );
        accessible.forEach((participant) => {
          const found = newAccessible.find((p) => p === participant.id);
          if (!found) {
            newAccessible.push(participant.id);
          }
        });
      }

      state.accessibleParticipants = newAccessible;
    },
    reset: () => initialState,
  },
});

export const {
  setParticipantsLoader,
  setParticipantsDetails,
  setParticipantsCount,
  setUnfilteredTotal,
  setParticipantsPage,
  setParticipantToggle,
  setParticipantSort,
  setParticipantFilter,
  setParticipantSearch,
  setColumnData,
  setCohortId,
  patchParticipantFilter,
  clearParticipantFilterField,
  clearAllParticipantFilters,
  clearParticipantIds,
  appendParticipantIds,
  appendAccessibleParticipants,
  reset,
} = participantsSlice.actions;

export default participantsSlice.reducer;
